import { Component, h, Prop, Event, EventEmitter, State } from "@stencil/core";
import { InterestResponse, NotificationsInterestsInfo, type FormEvent } from "../types";
import AuthHeader from "../auth-header";
import { requestNotificationsInterests, createInterestFollow, interestsStepFinished, requestInterests } from "../requests"
import { MIN_INTERESTS, MAX_INTERESTS } from "../../../utils/constants";
import type { Site } from '../../../types/common';

@Component({
  tag: 'hd-interests',
  styleUrl: './hd-interests.pcss',
  shadow: false
})
export class HdInterests {
  @Prop() site: Site = 'us';
  @Prop({ mutable: true }) selectedInterests: NotificationsInterestsInfo[] = [];
  @Prop() requestInterestsUrl: string = process.env.MY_AD_HOST + '/api/v1/users/onboarding/interests';

  @State() interests: InterestResponse;
  @State() isLoading: boolean = true;

  @Event() interestsChanged: EventEmitter<NotificationsInterestsInfo[]>;
  @Event() nextStep: EventEmitter<FormEvent>;

  async componentDidLoad() {
    this.isLoading = true;
    await this.getInterests();
    window.AD.getUser();
    this.isLoading = false;
  }

  private async getInterests() {
    try {
      const response = await requestInterests(
        this.requestInterestsUrl + `?site=${this.site}&type=all&limit=${MAX_INTERESTS}`
      );

      if (!response) {
        console.error("No interests data received");
        return null;
      }
      this.interests = response;
    }
    catch (error) {
      console.error("Error fetching interests:", error);
      return null;
    }
  }

  private saveInterests = (e?: Event) => {
    e?.preventDefault();
    if (this.selectedInterests.length >= MIN_INTERESTS) {
      this.selectedInterests.map(interest => createInterestFollow(`${process.env.NOTIFICATIONS_HOST}/follows`, interest));
      interestsStepFinished(`${process.env.MY_AD_HOST}/api/v1/users/tours/check`, null, 'interests_completed')
      this.nextStep.emit();
    }
  }

  private handleInterestSelection = async (interestSlug: string, interestType: string) => {
    let newSelectedInterests: NotificationsInterestsInfo[];
    let originalSelectedInterests = [...this.selectedInterests];

    // Optimistic update: Assume success and update UI immediately
    if (this.selectedInterests.some(item => item.slug === interestSlug)) {
      newSelectedInterests = this.selectedInterests.filter(i => i.slug !== interestSlug);
    } else {
      const tempInterest: NotificationsInterestsInfo = {
        id: null,
        slug: interestSlug,
        name: '',
        description: '',
        image: '',
        original_id: '',
        category: '',
        status: '',
        created_at: ''
      }
      newSelectedInterests = [...this.selectedInterests, tempInterest];
    }
    this.selectedInterests = newSelectedInterests;
    this.interestsChanged.emit(newSelectedInterests);

    try {
      if (!originalSelectedInterests.some(item => item.slug === interestSlug)) {
        const interestInfo = await requestNotificationsInterests(`${process.env.NOTIFICATIONS_HOST}/entities/${interestType}/${interestSlug}?site=us`);

        this.selectedInterests = this.selectedInterests.map(item =>
          item.slug === interestSlug ? interestInfo : item
        );
        this.interestsChanged.emit(this.selectedInterests);
      }
    } catch (error) {
      console.error("Failed to fetch interest info:", error);
      // Revert if the request fails
      this.selectedInterests = originalSelectedInterests;
      this.interestsChanged.emit(originalSelectedInterests);
    }
  };
  render() {
    return <form class="form-container step-container interests" onSubmit={this.saveInterests}>
      <AuthHeader
        title="Tell Us About Yourself"
        subhead="Help us personalize your ArchDaily experience with content and features that match your professional interests."
      />

      <div class="form-body">
        {this.isLoading ? (
          <picture>
            <source
              class="dark-alt"
              srcSet="https://assets.adsttc.com/doodles/flat/loader-white.gif"
              media="(prefers-color-scheme: dark)"
            />
            <img
              src="https://assets.adsttc.com/doodles/flat/loader-blue.gif"
              height={120}
              alt="Loading"
            />
          </picture>
        ) : (
          this.interests?.interests?.map(interest => (
            <label
              key={interest.slug}
              class={`interest-pill ${this.selectedInterests.some(item => item.slug === interest.slug) ? 'active' : ''}`}
            >
              <input
                type="checkbox"
                value={interest.slug}
                checked={this.selectedInterests.some(item => item.slug === interest.slug)}
                onChange={() => this.handleInterestSelection(interest.slug, interest.type)}
                class="visually-hidden"
                id={interest.slug}
              />{interest.name}
            </label>
          )))}
      </div>

      <div class="step-actions">
        <button class="next-step" type="submit" disabled={this.selectedInterests.length < MIN_INTERESTS}>
          Continue
        </button>
      </div>
    </form>
  }
}